/**
 * CSV Parser & Validator for Bulk CPT Import
 * 
 * Flexible column detection — accepts varied header names, ordering, and casing.
 * Rows with errors are separated out; valid rows can still be imported.
 */

import { ORTHOPEDIC_PROOF_MAPS } from '@/lib/proofmap/orthopedicRules';

export interface CsvRow {
  line: number;
  primary_cpt: string;
  subset_cpts: string[];
  payer_scope: string;
  notes: string;
}

export type ValidationSeverity = 'error' | 'warning' | 'info';

export interface ValidationIssue {
  line: number;
  field: string;
  severity: ValidationSeverity;
  message: string;
}

export interface ParsedImport {
  rows: CsvRow[];           // valid rows only
  errorRows: CsvRow[];      // rows that had errors
  issues: ValidationIssue[];
  proofMapCoverage: Record<string, boolean>;
  valid: boolean;           // true if there is at least 1 valid row
  totalCodes: number;
  uniqueCodes: string[];
}

const VALID_PAYER_SCOPES = ['all', 'commercial', 'workers_comp', 'medicaid', 'specific'];
const CPT_PATTERN = /^[A-Za-z0-9]{4,5}$/;

// ----- Flexible column matching -----

function normalizeColumnName(name: string): string {
  return name
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[_\s\-]+/g, ' ')
    .trim();
}

const COLUMN_ALIASES: Record<string, string[]> = {
  primary_cpt: ['primary cpt', 'primary', 'cpt', 'cpt code', 'primary code', 'code'],
  subset_cpts: ['subset cpts', 'subset', 'subsets', 'ancillary', 'bundle', 'bundle cpts', 'subset codes'],
  payer_scope: ['payer scope', 'payer', 'scope', 'payer type', 'coverage'],
  notes: ['notes', 'note', 'description', 'comments', 'comment'],
};

function findColumnIndex(headers: string[], field: string): number {
  const aliases = COLUMN_ALIASES[field] || [field];
  const normalizedHeaders = headers.map(h => normalizeColumnName(String(h || '')));

  // Priority 1: exact match
  for (const alias of aliases) {
    const idx = normalizedHeaders.indexOf(alias);
    if (idx !== -1) return idx;
  }
  // Priority 2: starts with
  for (const alias of aliases) {
    const idx = normalizedHeaders.findIndex(h => h.startsWith(alias));
    if (idx !== -1) return idx;
  }
  // Priority 3: contains
  for (const alias of aliases) {
    const idx = normalizedHeaders.findIndex(h => h.includes(alias));
    if (idx !== -1) return idx;
  }
  return -1;
}

export function parseCptCsv(csvText: string): ParsedImport {
  const lines = csvText.trim().split('\n');
  const rows: CsvRow[] = [];
  const errorRows: CsvRow[] = [];
  const issues: ValidationIssue[] = [];
  const allCodes = new Set<string>();

  if (lines.length < 2) {
    issues.push({ line: 1, field: 'file', severity: 'error', message: 'CSV must have a header row and at least one data row.' });
    return { rows, errorRows, issues, proofMapCoverage: {}, valid: false, totalCodes: 0, uniqueCodes: [] };
  }

  // Detect columns flexibly
  const headerCols = smartSplit(lines[0]).map(c => c.trim().replace(/"/g, ''));
  const colMap = {
    primary: findColumnIndex(headerCols, 'primary_cpt'),
    subset: findColumnIndex(headerCols, 'subset_cpts'),
    payer: findColumnIndex(headerCols, 'payer_scope'),
    notes: findColumnIndex(headerCols, 'notes'),
  };

  if (colMap.primary === -1) {
    // Fallback: assume first column is primary CPT
    colMap.primary = 0;
    issues.push({ line: 1, field: 'header', severity: 'warning', message: 'Could not detect "primary_cpt" column header — using first column.' });
  }
  if (colMap.subset === -1 && headerCols.length > 1) {
    colMap.subset = 1;
    issues.push({ line: 1, field: 'header', severity: 'warning', message: 'Could not detect "subset_cpts" column header — using second column.' });
  }

  // Parse data rows
  for (let i = 1; i < lines.length; i++) {
    const rawLine = lines[i].trim();
    if (!rawLine) continue;

    const cols = smartSplit(rawLine);
    const primary = (cols[colMap.primary] || '').trim().replace(/"/g, '');
    const subsetsRaw = colMap.subset >= 0 ? (cols[colMap.subset] || '').trim().replace(/^"|"$/g, '') : '';
    const payerScope = colMap.payer >= 0 ? (cols[colMap.payer] || '').trim().replace(/"/g, '').toLowerCase() : 'all';
    const notes = colMap.notes >= 0 ? (cols[colMap.notes] || '').trim().replace(/^"|"$/g, '') : '';

    let hasError = false;

    // Validate primary CPT
    if (!CPT_PATTERN.test(primary)) {
      issues.push({ line: i + 1, field: 'primary_cpt', severity: 'error', message: `Invalid CPT format: "${primary}"` });
      hasError = true;
    }

    // Parse subset CPTs
    const subsetCpts = subsetsRaw
      .split(',')
      .map(c => c.trim())
      .filter(Boolean);

    for (const sc of subsetCpts) {
      if (!CPT_PATTERN.test(sc)) {
        issues.push({ line: i + 1, field: 'subset_cpts', severity: 'error', message: `Invalid subset CPT: "${sc}"` });
        hasError = true;
      }
    }

    // Validate payer scope (warning only, doesn't block)
    if (payerScope && !VALID_PAYER_SCOPES.includes(payerScope)) {
      issues.push({ line: i + 1, field: 'payer_scope', severity: 'warning', message: `Unknown payer_scope "${payerScope}", defaulting to "all"` });
    }

    const row: CsvRow = { line: i + 1, primary_cpt: primary, subset_cpts: subsetCpts, payer_scope: payerScope || 'all', notes };

    if (hasError) {
      errorRows.push(row);
    } else {
      rows.push(row);
      allCodes.add(primary);
      subsetCpts.forEach(c => allCodes.add(c));
    }
  }

  // Check proof map coverage
  const proofMapCoverage: Record<string, boolean> = {};
  const proofMapCodes = new Set(ORTHOPEDIC_PROOF_MAPS.map(pm => pm.cpt_code));
  for (const code of allCodes) {
    proofMapCoverage[code] = proofMapCodes.has(code);
  }

  const unmapped = [...allCodes].filter(c => !proofMapCodes.has(c));
  if (unmapped.length > 0) {
    issues.push({
      line: 0,
      field: 'proof_map',
      severity: 'info',
      message: `${unmapped.length} code(s) have no Clinical Evidence Library rules yet: ${unmapped.slice(0, 8).join(', ')}${unmapped.length > 8 ? '…' : ''}`,
    });
  }

  return {
    rows,
    errorRows,
    issues,
    proofMapCoverage,
    valid: rows.length > 0, // valid as long as there are importable rows
    totalCodes: allCodes.size,
    uniqueCodes: [...allCodes].sort(),
  };
}

/** CSV-aware split that respects quoted fields */
function smartSplit(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (const ch of line) {
    if (ch === '"') {
      inQuotes = !inQuotes;
      current += ch;
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}
